import { useState } from "react";
import { ChapterHeader, FadeUp } from "./Motion";
import { toast } from "sonner";
import { api, trackEvent } from "@/lib/api";
import { Mail, Phone, User, ArrowUpRight } from "lucide-react";

const initial = {
    coachName: "",
    college: "",
    division: "",
    email: "",
    phone: "",
    message: "",
    requestTranscript: false,
    requestFullFootage: false,
    requestCoachReference: false,
    requestCall: false,
};

export default function ContactSection({ content }) {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            trackEvent("contact_submit");
            toast.success("Message sent — Jai'Rel and staff have been notified.");
            setForm(initial);
        } catch (err) {
            toast.error("Something went wrong. Please try again or email directly.");
        } finally {
            setSubmitting(false);
        }
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const cards = ["athlete", "parent", "hsCoach", "clubCoach"].map((k) => content.contacts[k]).filter(Boolean);

    return (
        <section id="contact" className="relative py-20 md:py-32 border-t border-white/10">
            <div className="absolute left-0 bottom-0 w-[600px] h-[600px] radial-purple pointer-events-none" />
            <div className="max-w-[1600px] mx-auto px-5 md:px-10 relative z-10">
                <ChapterHeader
                    num="09"
                    kicker="Direct Line"
                    title={`College Coaches — Contact ${content.athlete.firstName}`}
                    testId="chapter-contact"
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Form */}
                    <FadeUp className="lg:col-span-7">
                        <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {[
                                    { k: "coachName", label: "Coach Name", required: true },
                                    { k: "college", label: "College / University", required: true },
                                    { k: "division", label: "Division" },
                                    { k: "email", label: "Email", required: true, type: "email" },
                                    { k: "phone", label: "Phone" },
                                ].map((f) => (
                                    <label key={f.k} className="block">
                                        <span className="chapter-num">{f.label}{f.required && <span className="text-brand-gold"> *</span>}</span>
                                        <input
                                            type={f.type || "text"}
                                            required={f.required}
                                            value={form[f.k]}
                                            onChange={(e) => set(f.k, e.target.value)}
                                            className="mt-2 w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white outline-none focus:border-brand-gold transition-colors"
                                            data-testid={`field-${f.k}`}
                                        />
                                    </label>
                                ))}
                            </div>

                            <label className="block">
                                <span className="chapter-num">Message <span className="text-brand-gold">*</span></span>
                                <textarea
                                    required
                                    rows={4}
                                    value={form.message}
                                    onChange={(e) => set("message", e.target.value)}
                                    className="mt-2 w-full bg-transparent border border-white/10 p-3 font-mono text-sm text-white outline-none focus:border-brand-gold transition-colors resize-none"
                                    data-testid="field-message"
                                />
                            </label>

                            <div className="border-t border-white/10 pt-6">
                                <div className="chapter-num mb-4">Requests (optional)</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {[
                                        ["requestTranscript", "Request Transcript"],
                                        ["requestFullFootage", "Request Full Match Footage"],
                                        ["requestCoachReference", "Request Coach Reference"],
                                        ["requestCall", "Request a Call"],
                                    ].map(([k, label]) => (
                                        <label
                                            key={k}
                                            className="flex items-center gap-3 border border-white/10 p-3 hover:border-brand-gold/50 cursor-pointer transition-colors"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={form[k]}
                                                onChange={(e) => set(k, e.target.checked)}
                                                className="accent-[#F59E0B] w-4 h-4"
                                                data-testid={`checkbox-${k}`}
                                            />
                                            <span className="font-mono text-xs uppercase tracking-[0.15em]">{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <button type="submit" disabled={submitting} className="btn-gold" data-testid="submit-contact">
                                {submitting ? "Sending…" : "Send to Jai'Rel"} <ArrowUpRight size={14} />
                            </button>
                        </form>
                    </FadeUp>

                    {/* Contact cards */}
                    <FadeUp delay={0.1} className="lg:col-span-5">
                        <div className="grid grid-cols-1 gap-px bg-white/10 border border-white/10">
                            {cards.map((c, i) => (
                                <div key={i} className="p-5 md:p-6 bg-black" data-testid={`contact-card-${i}`}>
                                    <div className="chapter-num flex items-center gap-2">
                                        <User size={12} /> {c.role}
                                    </div>
                                    <div className="font-mono uppercase text-lg md:text-xl mt-2">{c.name}</div>
                                    {c.email && (
                                        <a
                                            href={`mailto:${c.email}`}
                                            className="flex items-center gap-2 mt-2 font-mono text-xs text-white/80 hover:text-brand-gold transition-colors break-all"
                                        >
                                            <Mail size={12} /> {c.email}
                                        </a>
                                    )}
                                    {c.phone && (
                                        <a
                                            href={`tel:${c.phone.replace(/\D/g, "")}`}
                                            className="flex items-center gap-2 mt-1 font-mono text-xs text-white/80 hover:text-brand-gold transition-colors"
                                        >
                                            <Phone size={12} /> {c.phone}
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}
